/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {
    Box,
    SimpleGrid,
    HStack,
    Text,
    Button,
    Center,
    useMultiStyleConfig,
    useStyleConfig,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    ADD_FILTER_HIT_COUNT,
    REMOVE_FILTER_HIT_COUNT
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getUrl} from '../../../utils/cloudinary-utils'

const ColorRefinements = ({filter, toggleFilter, selectedFilters, isMenu}) => {
    const intl = useIntl()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {storefrontPreferences} = commerceAPI.storefront || {}
    const {config} = commerceAPI.bloomreach || {}

    var colorImgs = {},
        metalImgs = {},
        staticBaseUrl = '',
        enableBloomreachSearch = null
    try {
        const customPrefsAsJson = JSON.parse(storefrontPreferences)
        const blrConfigAsJson = JSON.parse(config)
        colorImgs = customPrefsAsJson.colorImg
        metalImgs = customPrefsAsJson.metalImg
        staticBaseUrl = customPrefsAsJson.staticBaseUrl
        enableBloomreachSearch = blrConfigAsJson.enableBloomreachSearch
    } catch (e) {
        /** do nothing **/
    }
    const productListStyles = useStyleConfig('ProductListStyles')
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'circle',
        disabled: false,
        isRefinement: true
    })

    return (
        <SimpleGrid
            columns={1}
            spacing={0}
            mt={{base: isMenu ? '5px' : '0', lg: '0'}}
            maxHeight="300px"
            overflowY="auto"
            sx={{
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                '&::-webkit-scrollbar': {
                    width: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'gray.800',
                    borderRadius: '2px',
                    width: '4px',
                    display: 'block'
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'gray.400',
                    borderRadius: '2px',
                    width: '4px',
                    display: 'block'
                },
                userSelect: 'none',
                scrollBehavior: 'smooth'
            }}
        >
            {filter.values.map((value, idx) => {
                const isSelected = selectedFilters.includes(value.value)
                const nextValue = filter.values[idx + 1]
                const isNextSelected = nextValue && selectedFilters.includes(nextValue.value)
                // Don't display refinements with no results, unless we got there by selecting too
                // many refinements
                if (value.hitCount === 0 && !isSelected) return

                return (
                    <Box key={idx}>
                        <HStack
                            onClick={() => {
                                toggleFilter(value, filter.attributeId, isSelected)
                            }}
                            spacing={1}
                            borderColor={
                                isSelected ? 'gray.600 !important' : 'transparent !important'
                            }
                            borderBottomColor={
                                isSelected && !isNextSelected
                                    ? 'gray.600 !important'
                                    : 'transparent !important'
                            }
                            {...productListStyles.body.colorRefinements.stack}
                        >
                            <Button
                                {...styles.swatch}
                                aria-checked={isSelected}
                                border={isSelected ? '1px solid' : '0'}
                                {...productListStyles.body.colorRefinements.button}
                                aria-label={intl.formatMessage(
                                    isSelected ? REMOVE_FILTER_HIT_COUNT : ADD_FILTER_HIT_COUNT,
                                    value
                                )}
                            >
                                <Center
                                    {...styles.swatchButton}
                                    width={isSelected ? '10px' : '14px'}
                                    height={isSelected ? '10px' : '14px'}
                                    minWidth={isSelected ? '10px' : '14px'}
                                    marginRight={0}
                                >
                                    {enableBloomreachSearch ? (
                                        <Box
                                            style={{
                                                backgroundImage: `url('${staticBaseUrl}${
                                                    filter?.attributeId?.toLowerCase() === 'metal'
                                                        ? metalImgs?.[
                                                              value?.value
                                                                  ?.toLowerCase()
                                                                  ?.replace(/\b\w/g, (c) =>
                                                                      c?.toUpperCase()
                                                                  )
                                                          ]
                                                        : colorImgs?.[
                                                              value?.value
                                                                  ?.toLowerCase()
                                                                  ?.replace(/\b\w/g, (c) =>
                                                                      c?.toUpperCase()
                                                                  )
                                                          ]
                                                }')`
                                            }}
                                            {...productListStyles.body.colorRefinements.buttonBox}
                                        />
                                    ) : (
                                        <Box
                                            style={{
                                                backgroundImage: `url('${value.presentationId}')`
                                            }}
                                            {...productListStyles.body.colorRefinements.buttonBox}
                                        />
                                    )}
                                </Center>
                            </Button>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                                marginLeft="10px"
                            >
                                <Text
                                    {...productListStyles.body.colorRefinements.text}
                                    aria-hidden="true"
                                    color="black"
                                    textTransform="capitalize"
                                >
                                    {intl.formatMessage(
                                        {
                                            id: 'colorRefinements.label.hitCount',
                                            defaultMessage: '{colorLabel}'
                                        },
                                        {colorLabel: value.label}
                                    )}
                                </Text>
                                {isSelected ? (
                                    <Image
                                        alt="Cross Icon"
                                        width="15px"
                                        height="25px"
                                        className="cross-icon unselect-refinement"
                                        src={getUrl('UIImages/images/cross-gray.svg')}
                                    />
                                ) : (
                                    <></>
                                )}
                            </Box>
                        </HStack>
                    </Box>
                )
            })}
        </SimpleGrid>
    )
}

ColorRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array,
    isMenu: PropTypes.bool
}

export default React.memo(ColorRefinements)
